import {
    Desk,
    DeskRef,
    EmployeeRef,
    EntityType,
    Neighbourhood,
    NeighbourhoodSnapshot,
    SeatingPlan,
    SeatingPlanDeskAssignment,
    SeatingPlanForm,
    SeatingPlanMove,
    SeatingPlanNeighbourhoodUpdate,
    SeatingPlanNewNeighbourhood,
    SeatingPlanNewNeighbourhoodForm,
} from "./Model";
import {EntityStore, startEntityStore} from "./resourceStoreBuilder";
import Subscription from "../util/Subscription";
import {create} from "zustand";
import {deleteElement, replaceElement} from "../util/ArrayUtil";
import {neighbourhoodSubscription} from "./NeighbourhoodStore";
import {officeArrayResource} from "./CurrentOfficeStore";
import {SeatingPlans_Read} from "../util/access/Permission";
import {CrudRestResource} from "./RestResource";
import {emptyDesk} from "./Empty";

export const seatingPlanSubscription = new Subscription();

export interface SeatingPlanStore extends EntityStore<SeatingPlan[]> {
    createPlan: (form: SeatingPlanForm) => Promise<SeatingPlan>,
    createPlanFromSnapshot: (snapshot: NeighbourhoodSnapshot, form: SeatingPlanForm) => Promise<SeatingPlan>,
    updatePlan: (entity: SeatingPlan, form: SeatingPlanForm) => Promise<SeatingPlan>,
    deletePlan: (entity: SeatingPlan) => Promise<void>,

    setEmployeeMoves: (plan: SeatingPlan, employee: EmployeeRef, moves: SeatingPlanMove[]) => Promise<void>,
    setDeskAssignment: (plan: SeatingPlan, desk: DeskRef, assignment?: SeatingPlanDeskAssignment) => Promise<void>,

    createNewNeighbourhood: (plan: SeatingPlan, form: SeatingPlanNewNeighbourhoodForm) => Promise<void>,
    updateNewNeighbourhood: (plan: SeatingPlan, newNeighbourhood: SeatingPlanNewNeighbourhood, form: SeatingPlanNewNeighbourhoodForm) => Promise<void>,
    deleteNewNeighbourhood: (plan: SeatingPlan, newNeighbourhood: SeatingPlanNewNeighbourhood) => Promise<void>,

    updateNeighbourhoodUpdate: (plan: SeatingPlan, neighbourhood: Neighbourhood, form: SeatingPlanNewNeighbourhoodForm) => Promise<void>,
    deleteNeighbourhoodUpdate: (plan: SeatingPlan, neighbourhood: Neighbourhood) => Promise<void>,
}

function updateSeatingPlans(oldSeatingPlan: SeatingPlan, newSeatingPlan: Partial<SeatingPlan>): (prev: SeatingPlan[]) => SeatingPlan[] {
    return (prev: SeatingPlan[]) => replaceElement(prev, oldEntity => oldEntity === oldSeatingPlan, {...oldSeatingPlan, ...newSeatingPlan});
}

export const useSeatingPlanStore = create<SeatingPlanStore>()(
    officeArrayResource<SeatingPlan, SeatingPlanStore>(EntityType.SeatingPlans, builder => {
        const rest = builder.crud<SeatingPlanForm>('/seating-plans', 'id');
        const moveRest = rest.subresource<SeatingPlanMove[], EmployeeRef>('/moves', 'id');
        const deskAssignmentRest = rest.subresource<SeatingPlanDeskAssignment, Desk>('/desk-assignments', 'id');
        const newHoodsRest = rest.subresource<SeatingPlanNewNeighbourhoodForm, SeatingPlanNewNeighbourhood>('/new-neighbourhoods', 'id');
        const updHoodsRest = rest.subresource<SeatingPlanNewNeighbourhoodForm, SeatingPlanNeighbourhoodUpdate>('/update-neighbourhoods', 'neighbourhoodId');
        const snapshotsRest = new CrudRestResource<never, NeighbourhoodSnapshot>(
            `/offices/${builder.officeId}/neighbourhood-snapshots`,
            'id',
        );
        const snapshotPlansRest = snapshotsRest.subresource<SeatingPlanForm, SeatingPlan>('/seating-plans', 'id');

        return {
            remoteValue: builder.all(SeatingPlans_Read, () => rest.getAll()),
            createPlan: builder.createAndPatch<SeatingPlanForm>(rest, seatingPlanSubscription),
            createPlanFromSnapshot: async (snapshot, form) => {
                const plan = await snapshotPlansRest(snapshot).create(form);
                builder.patch(plans => [plan, ...plans]);
                seatingPlanSubscription.notify();
                return plan;
            },
            updatePlan: builder.updateAndPatch<SeatingPlanForm>(rest, seatingPlanSubscription),
            deletePlan: builder.deleteAndPatch<SeatingPlanForm>(rest, seatingPlanSubscription),

            setEmployeeMoves: async (plan: SeatingPlan, employee: EmployeeRef, moves: SeatingPlanMove[]) => {
                const updatedSeatingPlan = await moveRest(plan).updateCustom<SeatingPlan>(employee, moves);
                builder.patch(prevPlans =>
                    replaceElement(prevPlans, (oldEntity) => (oldEntity === plan), updatedSeatingPlan)
                );
                seatingPlanSubscription.notify()
            },

            setDeskAssignment: async (plan, desk, assignment) => {
                const fullDesk = {...emptyDesk, ...desk};
                if (assignment) {
                    const updatedPlan = await deskAssignmentRest(plan).updateCustom<SeatingPlan>(fullDesk, assignment);
                    // Concurrent responses contain snapshots of the entire plan. Merge only
                    // this desk so an older snapshot cannot undo other completed updates.
                    const updatedAssignment = updatedPlan.deskAssignments.find(it => it.desk.id === desk.id);
                    builder.patch(prev => prev.map(current => current.id === plan.id ? {
                        ...current,
                        ...(updatedPlan.updatedAt >= current.updatedAt ? {
                            updatedAt: updatedPlan.updatedAt,
                            updatedBy: updatedPlan.updatedBy,
                        } : {}),
                        deskAssignments: [
                            ...current.deskAssignments.filter(it => it.desk.id !== desk.id),
                            ...(updatedAssignment ? [updatedAssignment] : []),
                        ],
                    } : current));
                } else {
                    await deskAssignmentRest(plan).delete(fullDesk);
                    builder.patch(prev => prev.map(current => current.id === plan.id ? {
                        ...current,
                        deskAssignments: current.deskAssignments.filter(it => it.desk.id !== desk.id),
                    } : current));
                }
                seatingPlanSubscription.notify();
            },

            createNewNeighbourhood: async (plan: SeatingPlan, form: SeatingPlanNewNeighbourhoodForm) => {
                const createdNewNeighbourhood = await newHoodsRest(plan).create(form);
                builder.patch(updateSeatingPlans(plan, {
                    newNeighbourhoods: [...plan.newNeighbourhoods, createdNewNeighbourhood]
                }));
                seatingPlanSubscription.notify()
            },

            updateNewNeighbourhood: async (plan: SeatingPlan, newNeighbourhood: SeatingPlanNewNeighbourhood, form: SeatingPlanNewNeighbourhoodForm) => {
                const updatedNewNeighbourhood = await newHoodsRest(plan).update(newNeighbourhood, form);
                builder.patch(updateSeatingPlans(plan, {
                    newNeighbourhoods: replaceElement(plan.newNeighbourhoods, old => old.id === newNeighbourhood.id, updatedNewNeighbourhood)
                }));
                seatingPlanSubscription.notify()
            },

            deleteNewNeighbourhood: async (plan: SeatingPlan, newNeighbourhood: SeatingPlanNewNeighbourhood) => {
                await newHoodsRest(plan).delete(newNeighbourhood);
                builder.patch(updateSeatingPlans(plan, {
                    newNeighbourhoods: deleteElement(plan.newNeighbourhoods, newNeighbourhood)
                }));
                seatingPlanSubscription.notify()
            },

            updateNeighbourhoodUpdate: async (plan: SeatingPlan, neighbourhood: Neighbourhood, form: SeatingPlanNewNeighbourhoodForm) => {
                const hoodUpdate: SeatingPlanNeighbourhoodUpdate = {
                    neighbourhoodId: neighbourhood.id,
                    name: neighbourhood.name,
                    displayColor: neighbourhood.displayColor,
                    notes: neighbourhood.notes,
                };
                const updated = await updHoodsRest(plan).update(hoodUpdate, form);

                builder.patch(updateSeatingPlans(plan, {
                    neighbourhoodUpdates: [updated, ...plan.neighbourhoodUpdates.filter(upd => upd.neighbourhoodId !== updated.neighbourhoodId)]
                }));
                seatingPlanSubscription.notify();
            },

            deleteNeighbourhoodUpdate: async (plan: SeatingPlan, neighbourhood: Neighbourhood) => {
                const hoodUpdate: SeatingPlanNeighbourhoodUpdate = {
                    neighbourhoodId: neighbourhood.id,
                    name: neighbourhood.name,
                    displayColor: neighbourhood.displayColor,
                    notes: neighbourhood.notes,
                };
                await updHoodsRest(plan).delete(hoodUpdate);
                builder.patch(updateSeatingPlans(plan, {
                    neighbourhoodUpdates: plan.neighbourhoodUpdates.filter(upd => upd.neighbourhoodId !== neighbourhood.id)
                }));
                seatingPlanSubscription.notify();
            },
        };
    })
);

startEntityStore(useSeatingPlanStore, neighbourhoodSubscription);
